import { Euler, Matrix4, Quaternion } from 'three';
import { BALANCE } from '../config/balance';

export type MatrixLayout = 'row-major' | 'column-major';

/** Diagnostic rotation from the optional facial transformation matrix. */
export interface HeadRotation {
  yaw: number;
  pitch: number;
  roll: number;
}

export interface PoseExtractionOptions {
  /** Mirror the horizontal axis (selfie cameras are not mirrored by default). */
  mirrorX?: boolean;
  /**
   * How the flat 16-element array is laid out.
   *
   * MediaPipe does not document the layout of `facialTransformationMatrixes`.
   * The engine reads it as column-major by default, which is the behaviour
   * the game shipped with; the axis conventions themselves still need a real
   * webcam session to confirm (task `M4-2`, open question `Q6`).
   */
  layout?: MatrixLayout;
}

const scratchMatrix = new Matrix4();
const scratchQuaternion = new Quaternion();
const scratchEuler = new Euler(0, 0, 0, 'YXZ');

/**
 * Convert a facial transformation matrix into yaw/pitch/roll.
 *
 * The matrix is decomposed into a quaternion and read as a `YXZ` Euler:
 *   yaw   -> around the vertical axis,
 *   pitch -> around the lateral axis,
 *   roll  -> around the forward axis.
 *
 * Returns null when the matrix is missing or too short.
 */
export function poseFromTransformationMatrix(
  data: ArrayLike<number> | undefined | null,
  options: PoseExtractionOptions = {},
): HeadRotation | null {
  if (!data || data.length < 16) return null;

  const d = data;
  if ((options.layout ?? 'column-major') === 'column-major') {
    // `Matrix4.set` takes row-major arguments, so a column-major array has
    // to be gathered one column at a time.
    scratchMatrix.set(
      d[0],
      d[4],
      d[8],
      d[12],
      d[1],
      d[5],
      d[9],
      d[13],
      d[2],
      d[6],
      d[10],
      d[14],
      d[3],
      d[7],
      d[11],
      d[15],
    );
  } else {
    scratchMatrix.set(
      d[0],
      d[1],
      d[2],
      d[3],
      d[4],
      d[5],
      d[6],
      d[7],
      d[8],
      d[9],
      d[10],
      d[11],
      d[12],
      d[13],
      d[14],
      d[15],
    );
  }

  scratchQuaternion.setFromRotationMatrix(scratchMatrix);
  scratchEuler.setFromQuaternion(scratchQuaternion, 'YXZ');

  const mirror = options.mirrorX ? -1 : 1;
  return {
    yaw: mirror * scratchEuler.y,
    pitch: scratchEuler.x,
    roll: scratchEuler.z,
  };
}

export interface ViewExtractionOptions {
  /** Mirror the horizontal axis (selfie cameras are not mirrored by default). */
  mirrorX?: boolean;
  /** Normalised iris offset to metres. */
  scaleX?: number;
  scaleY?: number;
  /** Iris separation -> viewer distance, metres. */
  distance?: number;
}

/**
 * Estimate the viewer position from the face landmarks.
 *
 * MediaPipe FaceLandmarker returns 478 landmarks when the iris points are
 * available; 468 and 473 are the left and right iris centres. The horizontal
 * separation is a stand-in for distance, exactly as the off-axis demo does.
 */
export function viewFromLandmarks(
  landmarks: ArrayLike<{ x: number; y: number }> | undefined,
  options: ViewExtractionOptions = {},
): { x: number; y: number; z: number } | null {
  if (!landmarks || landmarks.length < 474) return null;
  const left = landmarks[468];
  const right = landmarks[473];
  if (!left || !right) return null;

  const cx = (left.x + right.x) / 2;
  const cy = (left.y + right.y) / 2;
  const separation = Math.hypot(left.x - right.x, left.y - right.y);
  if (separation < 1e-6) return null;

  const mirror = options.mirrorX ? -1 : 1;
  return {
    x: mirror * (0.5 - cx) * (options.scaleX ?? BALANCE.headTracking.viewScaleX),
    y: (0.5 - cy) * (options.scaleY ?? BALANCE.headTracking.viewScaleY),
    z: (options.distance ?? BALANCE.headTracking.viewDistance) / separation,
  };
}

/**
 * The head's position in the camera frame, normalised to 0..1.
 *
 * `0.5, 0.5` is the centre of the picture, in the raw frame the camera
 * delivers: the calibration preview is shown unmirrored, so a dot here always
 * sits on the face it was measured from. This is the raw point the calibration
 * map draws, before calibration, filtering or scaling.
 */
export function framePositionFromLandmarks(
  landmarks: ArrayLike<{ x: number; y: number }> | undefined,
): { x: number; y: number } | null {
  if (!landmarks || landmarks.length < 474) return null;
  const left = landmarks[468];
  const right = landmarks[473];
  if (!left || !right) return null;
  return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
}

/**
 * The iris separation, 0..1 of the frame width, or null without iris points.
 * `viewFromLandmarks` turns it into a distance proxy; the calibration wizard
 * reads it raw to estimate how far the player is sitting.
 */
export function separationFromLandmarks(
  landmarks: ArrayLike<{ x: number; y: number }> | undefined,
): number | null {
  if (!landmarks || landmarks.length < 474) return null;
  const left = landmarks[468];
  const right = landmarks[473];
  if (!left || !right) return null;
  return Math.hypot(left.x - right.x, left.y - right.y);
}

/**
 * Build a flat 16-element matrix from a three.js matrix, in the given layout.
 * Used by the tests to build a known input.
 */
export function flattenMatrix(matrix: Matrix4, layout: MatrixLayout = 'column-major'): number[] {
  const e = matrix.elements;
  if (layout === 'column-major') return Array.from(e);
  return [
    e[0],
    e[4],
    e[8],
    e[12],
    e[1],
    e[5],
    e[9],
    e[13],
    e[2],
    e[6],
    e[10],
    e[14],
    e[3],
    e[7],
    e[11],
    e[15],
  ];
}
